
import React from "react";
import { Movie } from "@/types/movie";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { MovieTitle, MovieYear } from "@/components/ui/typography";
import { getPosterUrl } from "@/utils/imageUtils";

interface MoviePosterProps {
  movie: Movie;
  isHovered: boolean;
}

const MoviePoster: React.FC<MoviePosterProps> = ({ 
  movie, 
  isHovered
}) => {
  // Get title overlay gradient for better readability
  const getTitleOverlayGradient = () => {
    // Only keeping the default state since we're removing hover effects
    return 'bg-gradient-to-t from-mono-eerieBlack/95 via-mono-eerieBlack/70 to-transparent';
  };

  // Determine text color with enhanced contrast
  const getTitleTextColor = () => {
    // Only keeping the default state since we're removing hover effects
    return 'text-mono-seasalt/95';
  };

  // Get the poster URL from either the full poster URL or poster_path
  const posterUrl = getPosterUrl(movie);

  return (
    <div className="relative transition-all duration-300 z-10">
      {/* Poster Image with shadow */}
      <div className="relative overflow-hidden rounded-lg transition-all duration-300 shadow-xl">
        <AspectRatio ratio={2/3} className="w-full">
          {posterUrl ? (
            <div className="w-full h-full relative overflow-hidden">
              <img
                src={posterUrl}
                alt={`${movie.title} poster`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center p-2 text-center 
              bg-gradient-to-br from-mono-slateGray/80 to-mono-outerSpace/60">
              <span className="text-sm font-bold tracking-tight text-mono-seasalt/95">
                {movie.title}
              </span>
            </div>
          )}
        </AspectRatio>

        {/* Title Overlay - only keeping opacity transition */}
        <div className={`absolute bottom-0 left-0 right-0 p-3 pt-16 
          transition-all duration-300 ${getTitleOverlayGradient()} ${isHovered ? 'opacity-0' : 'opacity-100'}`}>
          <MovieTitle className={`${getTitleTextColor()} truncate transition-all duration-300`}>
            {movie.title}
          </MovieTitle>
          <MovieYear className="transition-all duration-300">
            {movie.year}
          </MovieYear>
        </div>
      </div>
    </div>
  );
};

export default MoviePoster;
